import { useState } from "react";
import { X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { usePresetMutations, usePresets } from "../hooks";
import type { MetadataPreset } from "../types";
import { SectionToolbar, TableShell, Th } from "../components/ui";

function PresetFormModal({ preset, onClose }: { preset: MetadataPreset | null; onClose: () => void }) {
  const m = usePresetMutations();
  const [name, setName] = useState(preset?.name ?? "");
  const [cols, setCols] = useState<string[]>(() => (preset ? [...preset.columns] : [""]));
  const mutation = preset ? m.update : m.create;

  const submit = () => {
    const columns = cols.map((c) => c.trim()).filter(Boolean);
    if (!name.trim() || columns.length === 0) return;
    if (preset) {
      m.update.mutate({ id: preset.id, name: name.trim(), columns }, { onSuccess: onClose });
    } else {
      m.create.mutate({ name: name.trim(), columns }, { onSuccess: onClose });
    }
  };

  return (
    <Modal open onClose={onClose} title={preset ? "Edytuj preset" : "Nowy preset metadanych"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Nazwa presetu *</span>
          <input className="input-base mt-1" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
        </label>

        <div>
          <span className="section-label">Kolumny (etykiety)</span>
          <div className="space-y-1.5 mt-1">
            {cols.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="input-base text-sm py-1.5"
                  placeholder={`Kolumna ${i + 1}`}
                  value={c}
                  onChange={(e) => setCols((s) => s.map((x, j) => (j === i ? e.target.value : x)))}
                />
                <button
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  onClick={() => setCols((s) => (s.length > 1 ? s.filter((_, j) => j !== i) : [""]))}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setCols((s) => [...s, ""])}>+ Dodaj kolumnę</Button>
          </div>
        </div>

        {mutation.isError && (
          <p className="text-xs text-[var(--color-primary)] font-mono">Nie udało się zapisać presetu.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm"
            disabled={!name.trim() || cols.every((c) => !c.trim())}
            loading={mutation.isPending}
            onClick={submit}
          >
            {preset ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* Zakładka „Metadane" strony Presety (/power-projects/presety?tab=metadane).
   Tylko dyrektorzy/CEO — bramka na stronie Presety (PresetsPage) + backend. */
export function MetadataPresetsSection() {
  const { data, isLoading } = usePresets();
  const m = usePresetMutations();
  const [editing, setEditing] = useState<MetadataPreset | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="Szablony kolumn umowy — definiują dyrektorzy, koordynatorzy stosują w projekcie."
        actions={<Button size="sm" onClick={() => setCreating(true)}>+ Nowy preset</Button>}
      />

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Nazwa</Th>
              <Th>Kolumny</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((p) => (
              <tr key={p.id} className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
                <td className="px-3 py-2.5 font-medium text-[var(--color-text-primary)]">{p.name}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {p.columns.map((c, i) => (
                      <span key={i} className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">
                        {c}
                      </span>
                    ))}
                    {p.columns.length === 0 && <span className="section-label">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>Edytuj</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.confirm(`Usunąć preset „${p.name}"?`) && m.remove.mutate(p.id)}
                    >
                      Usuń
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {(data ?? []).length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-10 text-center section-label tracking-wide">
                  Brak presetów. Utwórz pierwszy szablon kolumn.
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <PresetFormModal preset={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
    </div>
  );
}
